import { useAppUserAccess } from 'global/cnr/contexts/useHelpers/useAppUserAccess';
import { _longPressTypes, LongPressWrapper } from 'global/wrappers/LongPressWrapper';
import { useContext, useMemo, useRef } from 'react';
import { Card } from 'semantic-ui-react';
import { getAppUserLink } from '../../../auth/accessPermissions';
import { AppSettingsContext } from '../../../cnr/contexts/AppSettingsContext';
import { ElementContext } from '../../../cnr/contexts/ElementContext';
import { ParentContext } from '../../../cnr/contexts/ParentContext';
import { UiItemContext } from '../../../cnr/contexts/UiItemContext';
import { uniqueKey } from '../../../common/keys';
import { gEnums } from '../../../enums/globalEnums';
import { _animateTypes } from '../../../motions/AnimateComponent';
import MotionComponent from '../../../motions/MotionComponent';
import UiImage from '../../imaging/UiImage';
import StarRating from '../../ratings/StarRating';
import CardExtra from '../CardExtra';
import BusScheduleLabels from './BusScheduleLabels';
import CardIndicator from './CardIndicator';
import CardLink from './CardLink';
import DateTimeLabels from './DateTimeLabels';
import LinkIcon from './LinkIcon';
import StatusLabels from './StatusLabels';
import TimeLabels from './TimeLabels';
import { groupTaggedPropSectionElements } from './CardElements';

const CardSchedule = () => {

  const _cds = gEnums.cardDisplayTypes

  // parentContext
  const parentContext = useContext(ParentContext);
  const { states, fns } = parentContext ?? {}
  const { eventInfo_state, currentSettings_state, paps_state, rating_state, transition_state } = states
  const { appUser_fns } = fns ?? {}
  const { pathViews, trueView } = paps_state ?? {}
  const { appDataDocuments, staticViews, staticViews_gs } = eventInfo_state ?? {}
  const { aps_global, aps_viewItems, aps_appUserSettings } = _.get(currentSettings_state, 'gvviSettings', {});
  const { appUserCollection } = aps_appUserSettings ?? {}
  const { appUserPermissions, appRating, display: display_global } = aps_global ?? {}
  const { showDirectLinks, showAllLinks } = display_global ?? {}
  const { links } = appUserPermissions ?? {}
  const { ratings } = rating_state ?? {}

  // appUserAccess
  const appUserAccess = useAppUserAccess()
  const { appUserSession, appUserSessionKey } = appUserAccess ?? {}

  // transitionContext  
  const { transitions } = transition_state ?? {}
  const transition = transitions ? transitions[_animateTypes.pageItemElement] : null
  const { showTransition } = transition ?? {}

  const appSettingsContext = useContext(AppSettingsContext);
  const { appSettings_state } = appSettingsContext ?? {}
  const { directLinksOverrideOn } = appSettings_state ?? {}

  // uiItemContext 
  const uiItemContext = useContext(UiItemContext)
  const { item_state, item_handlers } = uiItemContext ?? {}
  const { viewItem, _display, propSections_allowed, propGroups, _imageOpts, _viewItemOpts } = item_state ?? {}
  const { handleShow_modifyMapping } = item_handlers ?? {}
  const { cardDisplayType } = _display ?? {}
  const { showExtra, extraDisplayType, showExtraInternal } = _viewItemOpts ?? {}
  const { key: key_viewItem } = viewItem
  const { showItemImage, imageLocation: profileImageLocation, imageSize: profileImageSize } = _imageOpts ?? {}

  // elementContext
  const elementContext = useContext(ElementContext)
  const { element_state } = elementContext ?? {}
  const { itemData, elemProps, clickTo } = element_state ?? {}
  const { handleClick, cardProps, viewItem: viewItem_prop } = elemProps ?? {}
  const { scheduleType } = cardProps ?? {}
  const { _itemKey } = itemData ?? {}
  const { indicator } = viewItem_prop

  const { showIndicator, indicatorProp, indicatorLocation } = indicator ? indicator : {}
  const _indicatorLocation = indicatorLocation ? indicatorLocation : gEnums.bannerLocationTypes.bottom

  const _shows_card = {
    indicatorTop: showIndicator && indicatorProp && _indicatorLocation === gEnums.bannerLocationTypes.top,
    indicatorBottom: showIndicator && indicatorProp && _indicatorLocation === gEnums.bannerLocationTypes.bottom
  }

  let _hasDirectLink = false;
  let _isDirectLink = false;

  if (showDirectLinks && pathViews?.[trueView]) {
    const viewKey = pathViews?.[trueView]
    if (itemData?.[trueView] && itemData?.[trueView].includes(viewKey)) {
      _hasDirectLink = true
      if (directLinksOverrideOn) {
        _isDirectLink = true
      }
    }
  }

  const appUserLink = viewItem && links && getAppUserLink(appUserCollection, links, itemData, viewItem, appUserSession)

  const _staticViews = staticViews_gs ? staticViews_gs : staticViews
  const currentRating = ratings && ratings[_itemKey]

  const rating_access = appUser_fns.validateAccess_rating(currentRating, appRating, key_viewItem)
  const { allowAppUserView } = rating_access ?? {}

  let imageInContent = true
  const ignoreProps = ['startTime', 'startDate', 'endDate', 'endTime', 'locations', 'departureTime']
  const _keyy = uniqueKey('crds', 'sc', key_viewItem, itemData.id)

  const cardRef = useRef(null);

  switch (profileImageLocation) {
    case gEnums.imageLocations.top:
      imageInContent = false
      break;
    default:
    // nothing
  }

  const handleItemClick = (sss) => handleClick(viewItem, itemData._itemKey, null, { itemData: itemData, clickTo })

  const { cn, showFullImage } = useMemo(() => {
    let _cn = 'ui card fluid card-schedule';
    let _showFullImage = false;

    switch (cardDisplayType) {
      case _cds.profilePlus:
      case _cds.schedulePlus:
        _cn += ' plus';
        _showFullImage = true;
        break;
      case _cds.event:
        if (itemData?.eventStatusType) {
          _cn += ' ' + itemData.eventStatusType;
        }
        break;
      default:
      // nothing
    }

    return { cn: _cn, showFullImage: _showFullImage };
  }, [cardDisplayType, itemData?.eventStatusType]);

  // in useMemo
  const cardLink = () => <CardLink
    appUserLink={appUserLink}
    itemData={itemData}
  />

  // in useMemo
  const cardImage_top = () => <UiImage
    viewItem={viewItem}
    itemData={itemData}
    showFullImage={showFullImage}
  />

  // in useMemo
  const cardImage_inContent = () => <UiImage
    floated={profileImageLocation ? profileImageLocation : 'left'}
    size={profileImageSize ? profileImageSize : 'small'}
    viewItem={viewItem}
    itemData={itemData}
    fullImage={showFullImage}
  />

  // in useMemo
  const labels = (_keyy) => {
    switch (cardDisplayType) {
      case _cds.busSchedule:
        return <BusScheduleLabels itemData={itemData} />
      case _cds.event:
        return <StatusLabels itemData={itemData} />
      default:
        switch (scheduleType) {
          case 'list':
            return <TimeLabels _keyy={_keyy} itemData={itemData} propGroups={propGroups} />
          default:
            return <DateTimeLabels _keyy={_keyy} itemData={itemData} propGroups={propGroups} />
        }
    }
  }

  const _cardKeyy = uniqueKey('cpr', _itemKey)

  // in useMemo
  const cardIndicator = () => <CardIndicator key={uniqueKey('sc', 'cn', 'ind', _itemKey)} indicator={indicator} cardKey={_cardKeyy} itemData={itemData} aps_global={aps_global} staticViews={staticViews} />

  // in useMemo
  const card_extra = (_itemKey) => <CardExtra extraDisplayType={extraDisplayType} _itemKey={_itemKey} />

  const { nameElements, metaElements } = useMemo(() =>

    groupTaggedPropSectionElements({
      _keyy,
      appDataDocuments,
      appUserSession,
      aps_global,
      aps_viewItem: aps_viewItems && aps_viewItems[key_viewItem],
      display: _display,
      ignoreProps,
      itemData,
      propGroups,
      propsSections: propSections_allowed,
      staticViews: _staticViews,
      viewItemKey: key_viewItem,
      handleShow_modifyMapping
    }),
    [
      _keyy,
      appUserSession,
      aps_global,
      aps_viewItems,
      key_viewItem,
      _display,
      ignoreProps,
      itemData,
      propGroups,
      propSections_allowed,
      _staticViews,
      handleShow_modifyMapping
    ]);

  const cardContent = useMemo(() => (
    <>
      {showItemImage && !imageInContent && cardImage_top()}
      {scheduleType === 'list' && labels(_keyy)}
      <Card.Content>
        {_shows_card.indicatorTop && cardIndicator()}
        {showItemImage && imageInContent && cardImage_inContent()}

        <Card.Header key={uniqueKey('cs', 'h', _keyy)} as={'h5'}>{nameElements}</Card.Header>
        <Card.Meta key={uniqueKey('cs', 'm', _keyy)}>{metaElements}</Card.Meta>

        {allowAppUserView &&
          <Card.Meta key={uniqueKey('cs', 'sm', _keyy)}>
            <StarRating key={uniqueKey('ss', 'sr', _keyy)} allowRating={true} itemData={itemData} />
          </Card.Meta>
        }
        {(_hasDirectLink || showAllLinks) && <LinkIcon _keyy={_keyy} hasDirectLink={_hasDirectLink} showAllLinks={showAllLinks} />}
      </Card.Content>
      {scheduleType !== 'list' && labels(_keyy)}
      {appUserLink && appUserSessionKey && cardLink()}
      {showExtra && extraDisplayType && showExtraInternal && card_extra(_itemKey)}
      {_shows_card.indicatorBottom && cardIndicator()}
    </>
  ), [
    _hasDirectLink,
    _itemKey,
    _keyy,
    allowAppUserView,
    appUserLink,
    appUserSessionKey,
    directLinksOverrideOn,
    extraDisplayType,
    imageInContent,
    itemData,
    metaElements,
    nameElements,
    scheduleType,
    showAllLinks,
    showExtra,
    showExtraInternal,
    showItemImage,
  ]);

  const card_schedule = (contentOnly) => {

    return contentOnly ?
      cardContent
      :
      <div
        ref={cardRef}
        id={`crd-${_itemKey}`}
        itemkey={_itemKey}
        key={_keyy}
        className={cn}
        onClick={() => { handleItemClick(_itemKey) }}>
        {cardContent}
      </div>
  }

  const _showTransition = showTransition && cardDisplayType.indexOf('Plus') < 0

  if (_showTransition) {
    return <MotionComponent
      key={uniqueKey('mv', 'sep', key_viewItem)}
      transition={transition}
      oc={handleItemClick}
    >
      <LongPressWrapper
        itemData={itemData}
        longPressType={_longPressTypes.item}
      >
        {card_schedule()}
      </LongPressWrapper>
    </MotionComponent>
  } else {
    return <LongPressWrapper
      itemData={itemData}
      longPressType={_longPressTypes.item}
    >
      {card_schedule()}
    </LongPressWrapper>
  }

}

export default CardSchedule